import { useEffect } from 'react'
import { Link } from 'react-router-dom'

export const PrivacyPolicy = () => {
  useEffect(() => {
    document.body.style.overflowY = 'auto'
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto p-12">
        <div className="text-navy-900 flex flex-col gap-y-2 text-sm">
          <h1 className="font-semibold text-navy-900 text-4xl">Website Privacy Policy</h1>

          <p className="leading-8">Last Modified: April 27, 2022</p>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Introduction</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                DUPR (<span className="font-semibold">&quot;Company&quot;</span> or{' '}
                <span className="font-semibold">&quot;We&quot;</span>) respect your privacy and are committed to
                protecting it through our compliance with this policy.
              </p>
              <p className="leading-8">
                This policy describes the types of information we may collect from you or that you may provide when you
                visit the website{' '}
                <a href="https://mydupr.com" className="text-primary">
                  https://mydupr.com
                </a>{' '}
                (our <span className="font-semibold">&quot;Website&quot;</span>) and our practices for collecting,
                using, maintaining, protecting, and disclosing that information.
              </p>
              <p className="leading-8">This policy applies to information we collect:</p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">On this Website.</li>
                <li className="leading-8">
                  In email, text, and other electronic messages between you and this Website.
                </li>
                <li className="leading-8">
                  Through mobile and desktop applications you download from this Website or through applications made
                  available through mobile devices, which provide dedicated non-browser-based interaction between you
                  and this Website.
                </li>
                <li className="leading-8">
                  When you interact with our advertising and applications on third-party websites and services, if those
                  applications or advertising include links to this policy.
                </li>
              </ul>
              <p className="leading-8">It does not apply to information collected by:</p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">
                  Us offline or through any other means, including on any other website operated by Company or any third
                  party; or
                </li>
                <li className="leading-8">
                  Any third party, including through any application or content (including advertising) that may link to
                  or be accessible from or on the Website.
                </li>
              </ul>
              <p className="leading-8">
                Please read this policy carefully to understand our policies and practices regarding your information
                and how we will treat it. If you do not agree with our policies and practices, your choice is not to use
                our Website. By accessing or using this Website, you agree to this privacy policy. This policy may
                change from time to time (see Changes to Our Privacy Policy). Your continued use of this Website after
                we make changes is deemed to be acceptance of those changes, so please check the policy periodically for
                updates.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Children Under the Age of 13s</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                Our Website is not intended for children under 13 years of age. No one under age 13 may provide any
                information to or on the Website. We do not knowingly collect personal information from children under
                13. If you are under 13, do not use or provide any information on this Website or on or through any of
                its features. If we learn we have collected or received personal information from a child under 13
                without verification of parental consent, we will delete that information. If you believe we might have
                any information from or about a child under 13, please contact us at:{' '}
                <span className="text-primary-500">support@mydupr.com.</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Information We Collect About You and How We Collect It</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                We collect several types of information from and about users of our Website, including information:
              </p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">
                  By which you may be personally identified, such as name, postal address, e-mail address, telephone
                  number, or any other identifier by which you may be contacted online or offline ( "
                  <span className="font-semibold">personal information</span>");
                  <br />
                  That is about you but individually does not identify you; and/or About your internet connection, the
                  equipment you use to access our Website, and usage details.
                </li>
              </ul>
              <p className="leading-8">We collect this information:</p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">Directly from you when you provide it to us.</li>
                <li>
                  Automatically as you navigate through the site. Information collected automatically may include usage
                  details, IP addresses, and information collected through cookies.
                </li>
                <li>From third parties, for example, our business partners.</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Information You Provide to Us.</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">The information we collect on or through our Website may include:</p>
              <p className="leading-8 pl-5">
                Information that you provide by filling in forms on our Website. This includes information provided at
                the time of registering to use our Website, subscribing to our service, posting material, or requesting
                further services. We may also ask you for information when you enter a contest or promotion sponsored by
                us, and when you report a problem with our Website.
                <br />
                Records and copies of your correspondence (including email addresses), if you contact us.
                <br />
                Your responses to surveys that we might ask you to complete for research purposes.
                <br />
                Details of transactions you carry out through our Website and of the fulfillment of your orders. You may
                be required to provide financial information before placing an order through our Website.
                <br />
                Your search queries on the Website.
              </p>
              <p className="leading-8">
                You also may provide information to be published or displayed (hereinafter, "
                <span className="font-semibold">posted</span>") on public areas of the Website, or transmitted to other
                users of the Website or third parties (collectively, "
                <span className="font-semibold">User Contributions</span>"). Your User Contributions are posted on and
                transmitted to others at your own risk. Although we limit access to certain pages, please be aware that
                no security measures are perfect or impenetrable. Additionally, we cannot control the actions of other
                users of the Website with whom you may choose to share your User Contributions. Therefore, we cannot and
                do not guarantee that your User Contributions will not be viewed by unauthorized persons.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">
              Information We Collect Through Automatic Data Collection Technologies.
            </p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                As you navigate through and interact with our Website, we may use automatic data collection technologies
                to collect certain information about your equipment, browsing actions, and patterns, including:
              </p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">
                  Details of your visits to our Website, including traffic data, location data, logs, and other
                  communication data and the resources that you access and use on the Website.
                </li>
                <li className="leading-8">
                  Information about your computer and internet connection, including your IP address, operating system,
                  and browser type.
                </li>
              </ul>
              <p className="leading-8">
                The information we collect automatically does include personal information, or we may maintain it or
                associate it with personal information we collect in other ways or receive from third parties. It helps
                us to improve our Website and to deliver a better and more personalized service, including by enabling
                us to:
              </p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">Estimate our audience size and usage patterns.</li>
                <li className="leading-8">
                  Store information about your preferences, allowing us to customize our Website according to your
                  individual interests.
                </li>
                <li className="leading-8">Speed up your searches.</li>
                <li className="leading-8">Recognize you when you return to our Website.</li>
              </ul>
              <p className="leading-8">The technologies we use for this automatic data collection may include:</p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">
                  <span className="font-semibold">Cookies (or browser cookies).</span>
                  <br></br>A cookie is a small file placed on the hard drive of your computer. You may refuse to accept
                  browser cookies by activating the appropriate setting on your browser. However, if you select this
                  setting you may be unable to access certain parts of our Website. Unless you have adjusted your
                  browser setting so that it will refuse cookies, our system will issue cookies when you direct your
                  browser to our Website.
                </li>
                <li className="leading-8">
                  <span className="font-semibold">Flash Cookies.</span>
                  <br></br>Certain features of our Website may use local stored objects (or Flash cookies) to collect
                  and store information about your preferences and navigation to, from, and on our Website. Flash
                  cookies are not managed by the same browser settings as are used for browser cookies. For information
                  about managing your privacy and security settings for Flash cookies, see Choices About How We Use and
                  Disclose Your Information.
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Third-Party Use of Cookies</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                Some content or applications, including advertisements, on the Website are served by third-parties,
                including advertisers, ad networks and servers, content providers, and application providers. These
                third parties may use cookies to collect information about you when you use our website. The information
                they collect may be associated with your personal information or they may collect information, including
                personal information, about your online activities over time and across different websites and other
                online services. They may use this information to provide you with interest-based (behavioral)
                advertising or other targeted content.
              </p>

              <p className="leading-8">
                We do not control these third parties' tracking technologies or how they may be used. If you have any
                questions about an advertisement or other targeted content, you should contact the responsible provider
                directly. For information about how you can opt out of receiving targeted advertising from many
                providers, see Choices About How We Use and Disclose Your Information.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">How We Use Your Information</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                We use information that we collect about you or that you provide to us, including any personal
                information:
              </p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">To present our Website and its contents to you.</li>
                <li className="leading-8">
                  To provide you with information, products, or services that you request from us.
                </li>
                <li className="leading-8">To fulfill any other purpose for which you provide it.</li>
                <li className="leading-8">
                  To provide you with notices about your account, including expiration and renewal notices.
                </li>
                <li className="leading-8">
                  To carry out our obligations and enforce our rights arising from any contracts entered into between
                  you and us, including for billing and collection.
                </li>
                <li className="leading-8">
                  To notify you about changes to our Website or any products or services we offer or provide though it.
                </li>
                <li className="leading-8">
                  To notify you about changes to our Website or any products or services we offer or provide though it.
                </li>
                <li className="leading-8">In any other way we may describe when you provide the information.</li>
                <li className="leading-8">For any other purpose with your consent.</li>
              </ul>
              <p className="leading-8">
                We may also use your information to contact you about goods and services that may be of interest to you.
                If you do not want us to use your information in this way, please [check the relevant box located on the
                form on which we collect your data (the order form)][check the relevant box located on the form on which
                we collect your data (the registration form)][adjust your user preferences in your account profile]. For
                more information, see Choices About How We Use and Disclose Your Information.
              </p>

              <p className="leading-8">
                We may use the information we have collected from you to enable us to display advertisements to our
                advertisers' target audiences. Even though we do not disclose your personal information for these
                purposes without your consent, if you click on or otherwise interact with an advertisement, the
                advertiser may assume that you meet its target criteria.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Disclosure of Your Information</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">We may disclose aggregated information about our users without restriction.</p>
              <p className="leading-8">
                We may disclose personal information that we collect or you provide as described in this privacy policy:
              </p>
              <ul className="list-disc list-outside ml-8">
                <li className="leading-8">To our subsidiaries and affiliates.</li>
                <li className="leading-8">
                  To contractors, service providers, and other third parties we use to support our business.
                </li>
                <li className="leading-8">
                  To a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization,
                  dissolution, or other sale or transfer of some or all of DUPR's assets, whether as a going concern or
                  as part of bankruptcy, liquidation, or similar proceeding, in which personal information held by DUPR
                  about our Website users is among the assets transferred.
                </li>
                <li className="leading-8">
                  To third parties to market their products or services to you if you have not opted out of these
                  disclosures. For more information, see Choices About How We Use and Disclose Your Information.
                </li>
                <li className="leading-8">To fulfill the purpose for which you provide it.</li>
                <li className="leading-8">For any other purpose disclosed by us when you provide the information.</li>
                <li className="leading-8">With your consent.</li>
              </ul>
              <p className="leading-8">We may also disclose your personal information:</p>
              <p className="leading-8 px-5">
                To comply with any court order, law, or legal process, including to respond to any government or
                regulatory request.
                <br />
                To enforce or apply our Terms of Use and other agreements, including for billing and collection
                purposes.
                <br />
                If we believe disclosure is necessary or appropriate to protect the rights, property, or safety of DUPR,
                our customers, or othe
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Choices About How We Use and Disclose Your Information</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                <span className="font-semibold">How and why we use your information:</span> Contact you and provide you
                with information. For example, we might contact you through telephone, email, postal, notifications,
                text messages, or other communications, where permitted by applicable law, to provide you with service
                updates, promotional materials, surveys, offers, quotes, personalized information, and other information
                from us or our business partners.
              </p>
              <p className="leading-8">
                We strive to provide you with choices regarding the personal information you provide to us. We have
                created mechanisms to provide you with the following control over your information:
              </p>

              <p className="leading-8 pl-5">
                <span className="font-semibold">Tracking Technologies and Advertising.</span>
                <br /> You can set your browser to refuse all or some browser cookies, or to alert you when cookies are
                being sent. If you disable or refuse cookies, please note that some parts of this site may then be
                inaccessible or not function properly.
              </p>

              <p className="leading-8">
                We do not control third parties' collection or use of your information to serve interest-based
                advertising. However, these third parties may provide you with ways to choose not to have your
                information collected or used in this way. You can opt out of receiving targeted ads from members of the
                Network Advertising Initiative ("NAI") on the NAI's{' '}
                <Link
                  to="http://www.networkadvertising.org/managing/opt_out.asp"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-500 underline"
                >
                  website.
                </Link>
              </p>
              <p className="leading-8">
                California residents may have additional personal information rights and choices. Please see Your
                California Privacy Rights for more information.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Accessing and Correcting Your Information</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                You can review and change your personal information by logging into the Website and visiting your
                account profile page.
              </p>
              <p className="leading-8">
                If you delete your User Contributions from the Website, copies of your User Contributions may remain
                viewable in cached and archived pages, or might have been copied or stored by other Website users.
                Proper access and use of information provided on the Website, including User Contributions, is governed
                by the terms herein and the privacy policy. If you choose to remove your account, DUPR will replace your
                personal email address with a DUPR email address so the account can be claimed.
              </p>
              <p className="leading-8">
                California residents may have additional personal information rights and choices. Please see Your
                California Privacy Rights for more information.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Your California Privacy Rights</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                If you are a California resident, California law may provide you with additional rights regarding our
                use of your personal information.
              </p>
              <p className="leading-8">
                California's "Shine the Light" law (Civil Code Section § 1798.83) permits users of our App that are
                California residents to request certain information regarding our disclosure of personal information to
                third parties for their direct marketing purposes. To make such a request, please send an email to{' '}
                <span className="text-primary-500">support@mydupr.com.</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Your California Privacy Rights n</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                It is our policy to post any changes we make to our privacy policy on this page. If we make material
                changes to how we treat our users' personal information, we will notify you through a notice on the
                Website home page. The date the privacy policy was last revised is identified at the top of the page.
                You are responsible for ensuring we have an up-to-date active and deliverable email address for you, and
                for periodically visiting our Website and this privacy policy to check for any changes.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-y-2">
            <p className="leading-8 font-semibold underline">Contact Information</p>
            <div className="flex flex-col gap-y-1">
              <p className="leading-8">
                To ask questions or comment about this privacy policy and our privacy practices, contact us at:{' '}
                <span className="text-primary-500">support@mydupr.com.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
